// ============================================================
// A FIGURE'S STANDING STATES, DRAWN (plan design-gaps, S2; the ruling:
// "conditions are standing states on the blip"). engine/display/states gives
// each state a slot - the HEAD, the RING at the feet, the STALK - and this
// draws each there with a few strokes, in the plate's colour for it, animated
// in CSS only (DisplayStates.module.css: motion off holds every one still in
// its standing form). A WHOLE-figure state (unconscious, paralysed, fatigue,
// mute, helpless; a vehicle disabled, exploding, its weapon destroyed or its
// crew hit - S3) is not drawn here: it is a `data-state-<id>` mark on the
// marker's group, and its stylesheet dims, stills, blinks or acts on the
// vehicle's own parts (Unconscious and Helpless are already the figure's
// helpless posture, dimmed and breathing in Display.module.css: their marks
// add no second dim). A state that has just begun carries `data-onset` for
// 600 ms: its look grows in place from twice the size, settling (useOnsets),
// on a group of its own round the state's, so the state's own loop plays on
// under it. The 4-glyph status strip stays, as ruled.
// ============================================================
import type { ReactNode } from 'react';
import type { MapState } from '../../engine/display/states';
import type { LookPlace } from './looks/place';
import clsx from '../../utils/clsx';
import styles from '../../styles/DisplayStates.module.css';

type Draw = (p: LookPlace, level: number) => ReactNode;

const ellipse = (p: LookPlace, scale = 1) => ({ cx: p.foot.x, cy: p.foot.y, rx: p.rx * scale, ry: p.ry * scale });

/** Hatching across the ring: pinned under fire. */
const hatch: Draw = (p) => (
  <>
    {[-0.6, -0.2, 0.2, 0.6].map((k) => (
      <line
        key={k}
        x1={p.foot.x + p.rx * k - 4}
        y1={p.foot.y + p.ry * 0.7}
        x2={p.foot.x + p.rx * k + 4}
        y2={p.foot.y - p.ry * 0.7}
      />
    ))}
  </>
);

/** What each state draws in its slot. */
const DRAW: Readonly<Record<string, Draw>> = {
  // The head.
  stun: (p) => (
    <>
      <ellipse cx={p.head.x} cy={p.head.y - p.hr - 3} rx={p.hr * 1.1} ry={p.hr * 0.35} />
      {[0, 1, 2].map((i) => (
        <circle
          key={i}
          className={styles.orbit}
          cx={p.head.x + (i - 1) * p.hr * 0.8}
          cy={p.head.y - p.hr - 3}
          r={1.6}
        />
      ))}
    </>
  ),
  blind: (p) => <circle className={styles.filled} cx={p.head.x} cy={p.head.y} r={p.hr * 0.95} />,
  halluc: (p, level) => <circle cx={p.head.x + 3 + level} cy={p.head.y - 2} r={p.hr} />,
  deaf: (p) => <circle cx={p.head.x} cy={p.head.y} r={p.hr * 1.5} />,
  engDmg: (p) => (
    <>
      {[0, 1, 2].map((i) => (
        <circle
          key={i}
          className={styles.rise}
          cx={p.head.x + (i - 1) * 4}
          cy={p.head.y - p.hr - 4 - i * 4}
          r={2 + i}
        />
      ))}
    </>
  ),
  // The ring.
  pinned: hatch,
  snared: (p) => (
    <path
      d={`M ${p.foot.x - p.rx} ${p.foot.y} Q ${p.foot.x} ${p.foot.y - p.ry * 1.6} ${p.foot.x + p.rx} ${p.foot.y} Q ${p.foot.x} ${p.foot.y + p.ry * 1.6} ${p.foot.x - p.rx} ${p.foot.y}`}
    />
  ),
  immob: (p) => <ellipse {...ellipse(p, 1.15)} />,
  fire: (p) => (
    <>
      {[-0.5, 0, 0.5].map((k) => (
        <path
          key={k}
          className={styles.flicker}
          d={`M ${p.foot.x + p.rx * k - 3} ${p.foot.y} Q ${p.foot.x + p.rx * k} ${p.foot.y - 12} ${p.foot.x + p.rx * k + 3} ${p.foot.y}`}
        />
      ))}
    </>
  ),
  unaware: (p) => <ellipse {...ellipse(p, 1.3)} />,
  hullBr: (p) => <ellipse {...ellipse(p, 1.1)} />,
  // The stalk.
  dying: (p) => <circle className={styles.beat} cx={p.foot.x} cy={(p.foot.y + p.head.y) / 2} r={2.5} />,
  haywire: (p, level) => {
    const mid = (p.foot.y + p.head.y) / 2;
    const w = 2 + level;
    return (
      <path
        d={`M ${p.foot.x} ${p.head.y + p.hr} L ${p.foot.x + w} ${mid - 4} L ${p.foot.x - w} ${mid + 4} L ${p.foot.x} ${p.foot.y}`}
      />
    );
  },
  bloodLoss: (p, level) => (
    <>
      {Array.from({ length: level + 1 }, (_, i) => (
        <circle key={i} className={styles.drip} cx={p.foot.x + (i - level / 2) * 3} cy={p.foot.y + 3 + i * 2} r={1.4} />
      ))}
    </>
  ),
};

/** The figure's states drawn in their slots; null when it wears none there. */
export default function TokenStates(props: {
  id: string;
  states: readonly MapState[];
  place: LookPlace;
  /** The states that have just begun (useOnsets): drawn large and settling for 600 ms. */
  onsets?: readonly string[];
}) {
  const { id, states, place, onsets } = props;
  // A whole-figure state has no drawing here: it is the marker's own mark (`wholeStatesOf`).
  const drawn = states.filter((s) => DRAW[s.id]);
  if (!drawn.length) return null;
  return (
    <g data-testid={`token-states-${id}`} className={styles.states} pointerEvents="none">
      {drawn.map((s) => {
        const onset = onsets?.includes(s.id);
        return (
          <g
            key={s.id}
            data-state={s.id}
            data-slot={s.slot}
            data-onset={onset || undefined}
            className={clsx(onset && styles.onset)}
          >
            <g className={clsx(styles.state, styles[`st_${s.id}`])}>{DRAW[s.id](place, s.level)}</g>
          </g>
        );
      })}
    </g>
  );
}

/** An id as an attribute's name: lowercase, a camelCase id in kebab (`wepDest` -> `wep-dest`) - React warns otherwise. */
const kebab = (id: string) => id.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/** The whole-figure states, as the marker's group wears them: `data-state-<id in kebab>="true"`. */
export function wholeStatesOf(states: readonly MapState[] | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of states ?? []) if (s.slot === 'whole') out[`data-state-${kebab(s.id)}`] = 'true';
  return out;
}
